import {
	type SupportedLanguage,
	resolveLanguagePreference,
} from "./language";

export const AUTH_MODE =
	(import.meta as any).env.VITE_AUTH_MODE || "centralized";
const normalizeDomain = (value: string | undefined): string => {
	if (!value) return "";
	const withoutProtocol = value.replace(/^https?:\/\//i, "");
	const hostOnly = withoutProtocol.split("/")[0];
	return hostOnly.trim();
};
export const AUTH_DOMAIN =
	normalizeDomain((import.meta as any).env.VITE_AUTH_DOMAIN) ||
	"localhost:5174";
export const MAIN_DOMAIN =
	(import.meta as any).env.VITE_MAIN_DOMAIN || "localhost:5173";
export const COOKIE_DOMAIN = (import.meta as any).env.VITE_COOKIE_DOMAIN;
const MIXPANEL_BRIDGE_COOKIE = "mp_bridge_id";
const MIXPANEL_BRIDGE_MAX_AGE_SECONDS = 120;

export const isSubdomainMode = () => AUTH_MODE === "subdomain";

const getCurrentLanguage = (): SupportedLanguage => resolveLanguagePreference();

const setMixpanelBridgeCookie = (userId: string) => {
	try {
		const cookieParts = [
			`${MIXPANEL_BRIDGE_COOKIE}=${encodeURIComponent(userId)}`,
			"Path=/",
			`Max-Age=${MIXPANEL_BRIDGE_MAX_AGE_SECONDS}`,
			"SameSite=Lax",
		];

		if (window.location.protocol === "https:") {
			cookieParts.push("Secure");
		}

		if (COOKIE_DOMAIN && COOKIE_DOMAIN !== "localhost") {
			cookieParts.push(`Domain=${COOKIE_DOMAIN}`);
		} else {
			console.warn(
				"[AUTH_MODE] VITE_COOKIE_DOMAIN not set for shared domain; Mixpanel bridge cookie may not be readable on main app subdomain",
			);
		}

		document.cookie = cookieParts.join("; ");
	} catch (error) {
		console.warn(
			"[AUTH_MODE][MIXPANEL_BRIDGE] Failed to set bridge cookie",
			error,
		);
	}
};

export const getAuthRedirectUrl = (path: string, redirectUrl?: string) => {
	if (isSubdomainMode()) {
		const protocol = window.location.protocol;
		const authUrl = `${protocol}//${AUTH_DOMAIN}${path}`;
		return redirectUrl
			? `${authUrl}?redirect=${encodeURIComponent(redirectUrl)}`
			: authUrl;
	}
	return redirectUrl
		? `${path}?redirect=${encodeURIComponent(redirectUrl)}`
		: path;
};

export const redirectAfterAuth = (nextUrl?: string, token?: string) => {
	if (isSubdomainMode()) {
		const protocol = window.location.protocol;
		const targetUrl = nextUrl || `${protocol}//${MAIN_DOMAIN}`;
		const urlWithToken = token ? `${targetUrl}?token=${token}` : targetUrl;
		window.location.href = urlWithToken;
	} else {
		// Use React Router navigate (caller handles this)
		return nextUrl || "/main";
	}
};

/**
 * Redirect to main app with tenant information
 * Auth tokens are automatically shared via cookies
 */
export const redirectToMainApp = (options?: {
	tenantId?: string;
	tenantDomain?: string;
	redirectPath?: string;
	userId?: string;
	lang?: SupportedLanguage;
}) => {
	if (isSubdomainMode()) {
		const protocol = window.location.protocol;
		// Prefer tenant custom domain, fallback to main domain
		const targetDomain = options?.tenantDomain || MAIN_DOMAIN;
		const targetUrl = `${protocol}//${targetDomain}`;

		const params = new URLSearchParams();

		// Add redirect parameter if provided
		if (options?.redirectPath) {
			params.set("redirect", options.redirectPath);
		}

		const language = options?.lang || getCurrentLanguage();
		if (language) {
			params.set("lang", language);
		}

		// Pass user ID to main app via short-lived cookie for Mixpanel identity continuity.
		// Avoid query params so edge/WAF rules don't block redirects.
		if (options?.userId) {
			setMixpanelBridgeCookie(options.userId);
		} else {
			console.warn(
				"[AUTH_MODE] No userId provided - bridge cookie will not be set",
			);
		}

		const queryString = params.toString();
		const finalUrl = queryString ? `${targetUrl}?${queryString}` : targetUrl;

		window.location.href = finalUrl;
	}
};

/**
 * Redirect unauthenticated users to the auth subdomain
 */
export const redirectToAuthSubdomain = (fullPath?: string) => {
	if (isSubdomainMode()) {
		const protocol = window.location.protocol;
		const authUrl = fullPath
			? `${protocol}//${AUTH_DOMAIN}${fullPath}`
			: `${protocol}//${AUTH_DOMAIN}/auth`;

		window.location.href = authUrl;
	}
};

export const redirectToSubdomain = (
	url: string,
	options?: { replace?: boolean },
) => {
	if (isSubdomainMode()) {
		const protocol = window.location.protocol;
		const authUrl = `${protocol}//${MAIN_DOMAIN}${url}`;
		if (options?.replace) {
			window.location.replace(authUrl);
		} else {
			window.location.href = authUrl;
		}
	}
};
